import { useAuth0 } from '@auth0/auth0-react';
import {
  ClientNavigationBar,
  type ClientNavigationAuth,
  type ClientNavigationItem,
} from '@ths/ui';
import { useLocation, useNavigate } from 'hotel/navigation';

import { useTravelerSelfCheck } from 'hooks/useTravelerSelfCheck';
import { useAppSelector } from 'store/store';

import { ClientSwitcher } from '../hotel/ClientSwitcher';
import { hotelPath, useHotelScope } from '../hotel/scope';
import { useOptionalHotelSession } from '../hotel/session';

const standardNavigation = [
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'Trips', href: '/trips' },
  { label: 'Live Roster', href: '/roster' },
  { label: 'Requests', href: '/requests' },
  { label: 'Contact THS', href: '/contact' },
];

export default function Header() {
  const { canSelfBook } = useTravelerSelfCheck();
  const savedCardsEnabled = useAppSelector(
    (state) => state.enums.featureFlags?.flags.hotelSavedCards === true,
  );
  const location = useLocation();
  const scope = useHotelScope();
  const session = useOptionalHotelSession();
  const navigate = useNavigate();
  const { user, logout, loginWithRedirect, isLoading } = useAuth0();

  const navigationItems: ClientNavigationItem[] = (
    scope ? standardNavigation : []
  ).map((item) => ({
    ...item,
    href: hotelPath(scope?.clientId, item.href),
    current: location.pathname.startsWith(item.href),
  }));

  const auth: ClientNavigationAuth = isLoading
    ? { kind: 'loading' }
    : user
      ? {
          kind: 'signed-in',
          user: {
            displayName:
              user.name ??
              user['https://www.teamhousing.com/auth/username'] ??
              user.email ??
              'Client',
            email: user.email ?? '',
            avatarUrl: user.picture,
          },
          onSignOut: () => {
            const action = () =>
              logout({ logoutParams: { returnTo: window.location.origin } });
            if (session) session.requestSignOut(action);
            else void action();
          },
        }
      : {
          kind: 'signed-out',
          onSignIn: () => void loginWithRedirect(),
        };

  return (
    <ClientNavigationBar
      auth={auth}
      accountItems={
        scope && savedCardsEnabled
          ? [
              {
                href: hotelPath(scope.clientId, '/payment-methods'),
                label: 'Payment methods',
              },
            ]
          : []
      }
      homeHref={hotelPath(scope?.clientId, '/dashboard')}
      navigationItems={navigationItems}
      primaryAction={
        scope && canSelfBook
          ? {
              href: hotelPath(scope.clientId, '/dashboard'),
              label: 'Start a trip',
            }
          : undefined
      }
      onNavigate={navigate}
      contextControl={scope && session ? <ClientSwitcher /> : undefined}
    />
  );
}
